import { cssVar, Grid, GridProps } from '@cad-arc';
import { CadNextNotification, CadNextNotificationPriorityEnumType } from '@mark43/cad-api';
import { computed } from 'mobx';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { AedStore } from '../../automatic-event-dispatch/aed-store';
import { useAedStore } from '../../automatic-event-dispatch/use-aed-store';
import { eventNotificationPriorityToColors } from '../../event-notifications/event-notifications';
import { EventNotificationsEntityIdProvider } from '../../event-notifications/event-notifications-entity-id-context';
import { EventNotificationsEventIdProvider } from '../../event-notifications/event-notifications-event-id-context';
import { getHighestPriority } from '../../event-notifications/get-highest-priority';
import { useAllEventNotifications } from '../../event-notifications/use-event-notifications';
import { WorkspaceQueue } from '../../panels/workspace-queue';
import { EventQueueRowData, UnitQueueRowData } from '../../socket/stream-types';
import columnDefinitionEnum from '../column-definition.enum';
import { useContextMenuStateWrite } from '../context/use-context-menu-state';
import { useQueueStore } from '../context/use-queue-store';
import { QueueStore } from '../store/queue-store';
import { RawQueueRowData } from '../store/types-2';
import { getEventQueueRowData } from '../utils/get-event-queue-row-data';
import { useIsUnitTimerAlerting } from '../utils/use-is-unit-timer-alerting';
import { BaseQueueCell } from './base-queue-cell';
import { QueueCell } from './queue-cell';
import { QueueCellProvider } from './queue-cell-context';
import { unitStatusColors } from '../../common/helpers/styles';
import { useDraggableQueueRow } from '../hooks/use-draggable-queue-row';
import {
    UNIT_EMERGENCY_BACKGROUND_COLOR,
    UNIT_EMERGENCY_TEXT_COLOR,
} from '../../unit-emergency/unit-emergencies-constants';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { colors } from '../../colors/colors';

import priorityFlash from '../../assets/images/priority_flash.png';
import unitFlash from '../../assets/images/unit_flash.png';

function PinToPrimaryBottomBorder(props: React.HTMLAttributes<HTMLDivElement>) {
    return (
        <div
            {...props}
            style={{
                position: 'absolute',
                width: '18px',
                left: 0,
                bottom: 0,
                borderBottom: '3px solid ' + colors.backupUnitBorder,
                ...props.style,
            }}
        >
            {props.children}
        </div>
    );
}

export const HIGHLIGHTED_ROW_BG = 'interactive.hover';

type EventQueueBackgroundProps = {
    backgroundColor?: GridProps['backgroundColor'];
    backgroundImage?: GridProps['backgroundImage'];
    color?: GridProps['color'];
};

function getEventQueueRowBackgroundProps(
    queueStore: QueueStore,
    aedStore: AedStore,
    data: EventQueueRowData,
    isRowSelected: boolean,
    eventNotificationPriority: CadNextNotificationPriorityEnumType | undefined,
    isRowHoveredOnDrag: boolean
): EventQueueBackgroundProps {
    const props: EventQueueBackgroundProps = {};

    if (queueStore.workspacePanel.queue.dataEntityType === 'CAD_AGENCY_EVENT') {
        const showAedFailure = data.aedState === 'FAILED' && !data.primaryUnitId;
        const showHighlight = isRowSelected || isRowHoveredOnDrag;

        const showHighPriorityHighlight =
            queueStore.workspacePanel.queue.fullRowUpdates && eventNotificationPriority === 'HIGH';
        const showStandardPriorityHighlight =
            queueStore.workspacePanel.queue.fullRowUpdates &&
            eventNotificationPriority === 'STANDARD';

        if (showHighlight && showAedFailure) {
            props.backgroundColor = colors.aedFailureHighlighted;
            props.color = colors.unitStatusTextLight;
        } else if (showHighlight) {
            props.backgroundColor = HIGHLIGHTED_ROW_BG;
        } else if (showAedFailure && showHighPriorityHighlight) {
            props.backgroundColor = colors.aedFailureHigh;
            props.color = colors.unitStatusTextLight;
        } else if (showAedFailure) {
            props.backgroundColor = colors.aedFailureStandard;
            props.color = colors.unitStatusTextLight;
        } else if (showHighPriorityHighlight) {
            props.backgroundColor = eventNotificationPriorityToColors.HIGH.highlight;
            props.color = eventNotificationPriorityToColors.HIGH.notchOrText;
        } else if (showStandardPriorityHighlight) {
            props.backgroundColor = eventNotificationPriorityToColors.STANDARD.highlight;
            props.color = eventNotificationPriorityToColors.STANDARD.notchOrText;
        }

        if (data.aedState === 'ATTEMPTING' || data.aedState === 'ACCEPTING') {
            props.backgroundColor = showHighlight ? HIGHLIGHTED_ROW_BG : undefined;
            props.backgroundImage = priorityFlash;
        }
    }

    return props;
}

function getUnitQueueRowBackgroundProps(
    queueStore: QueueStore,
    data: UnitQueueRowData,
    isRowSelected: boolean,
    isAlerting: boolean,
    eventNotificationPriority: CadNextNotificationPriorityEnumType | undefined,
    isRowHoveredOnDrag: boolean,
    unitEmergencyEnabled: boolean
): EventQueueBackgroundProps {
    const props: EventQueueBackgroundProps = {};

    if (queueStore.workspacePanel.queue.dataEntityType === 'UNIT') {
        if (data.hasInProgressEmergency && unitEmergencyEnabled) {
            props.backgroundColor = UNIT_EMERGENCY_BACKGROUND_COLOR;
            props.color = UNIT_EMERGENCY_TEXT_COLOR;
        } else if (isRowSelected || isRowHoveredOnDrag) {
            props.backgroundColor = HIGHLIGHTED_ROW_BG;
        } else if (
            queueStore.workspacePanel.queue.fullRowUpdates &&
            eventNotificationPriority === 'HIGH'
        ) {
            props.backgroundColor = eventNotificationPriorityToColors.HIGH.highlight;
            props.color = eventNotificationPriorityToColors.HIGH.notchOrText;
        } else if (
            queueStore.workspacePanel.queue.fullRowUpdates &&
            eventNotificationPriority === 'STANDARD'
        ) {
            props.backgroundColor = eventNotificationPriorityToColors.STANDARD.highlight;
            props.color = eventNotificationPriorityToColors.STANDARD.notchOrText;
        } else if (queueStore.workspacePanel.queue.pinBackupsToPrimaryUnits && data.hasBackups) {
            props.backgroundColor = unitStatusColors[data.unitStateGroup!].fill;
            props.color = unitStatusColors[data.unitStateGroup!].text;
        }

        if (isAlerting && queueStore.workspacePanel.queue.fullRowUnitTimerUpdates) {
            props.backgroundImage = unitFlash;
        }
    }

    return props;
}

function getScheduledQueueRowBackgroundProps(
    queueStore: QueueStore,
    isRowSelected: boolean,
    isRowHoveredOnDrag: boolean
): EventQueueBackgroundProps {
    const props: EventQueueBackgroundProps = {};

    if (
        queueStore.workspacePanel.queue.dataEntityType === 'SCHEDULED_AGENCY_EVENT' &&
        (isRowSelected || isRowHoveredOnDrag)
    ) {
        props.backgroundColor = HIGHLIGHTED_ROW_BG;
    }

    return props;
}

/**
 * Main component responsible for rendering a queue row
 */
export const QueueRow = React.memo(
    observer(function QueueRow({
        data,
        queueColumns,
        isSelected,
        onRowSelect,
        onDoubleClick,
        index,
    }: {
        data: RawQueueRowData;
        queueColumns: WorkspaceQueue['queueColumns'];
        isSelected: (rowId: number) => boolean;
        onRowSelect: (rowId: number) => void;
        onDoubleClick: (data: RawQueueRowData) => void;
        index: number;
    }): React.ReactElement {
        const aedStore = useAedStore();
        const enabledFeatures = useEnabledFeatures();
        const isRowSelected = computed(() => isSelected(data.id)).get();
        // TODO fix this in a followup - https://mark43.atlassian.net/browse/CAD-15834
        const queueStore = useQueueStore();
        const contextMenuStateWrite = useContextMenuStateWrite();
        const onClickMemo = React.useCallback(() => {
            onRowSelect(data.id);
        }, [onRowSelect, data]);
        const onDoubleClickMemo = React.useCallback(() => {
            onDoubleClick(data);
        }, [onDoubleClick, data]);
        const eventNotificationMapsForRow = useAllEventNotifications(
            getEventQueueRowData(data)?.id ?? -1
        );
        const eventNotificationsForRow: CadNextNotification[] = Object.values(
            eventNotificationMapsForRow ?? {}
        );
        const eventNotificationPriority: CadNextNotificationPriorityEnumType | undefined =
            eventNotificationsForRow.reduce(
                (acc, member) => getHighestPriority(acc, member.notificationPriority),
                undefined as CadNextNotificationPriorityEnumType | undefined
            );
        const entityType = queueStore.workspacePanel.queue.dataEntityType;
        const onContextMenu = React.useCallback(() => {
            onRowSelect(data.id);
            switch (entityType) {
                case 'CAD_AGENCY_EVENT': {
                    contextMenuStateWrite({ kind: 'EVENT', entityId: data.id });
                    break;
                }
                case 'SCHEDULED_AGENCY_EVENT': {
                    contextMenuStateWrite({ kind: 'SCHEDULED_EVENT', data });
                    break;
                }
                case 'UNIT': {
                    contextMenuStateWrite({ kind: 'UNIT', entityId: data.id });
                    break;
                }
                case 'HOSPITAL': {
                    contextMenuStateWrite({ kind: 'HOSPITAL', data });
                    break;
                }
                case 'BOLO': {
                    contextMenuStateWrite({ kind: 'BOLO', data });
                    break;
                }
            }
        }, [entityType, data, onRowSelect, contextMenuStateWrite]);

        const isAlerting = useIsUnitTimerAlerting(queueStore, data);
        const isUnitQueue = queueStore.workspacePanel.queue.dataEntityType === 'UNIT';
        const isEventQueue = queueStore.workspacePanel.queue.dataEntityType === 'CAD_AGENCY_EVENT';
        const aedStateAttr = isUnitQueue
            ? {}
            : { 'data-aed-state': (data as EventQueueRowData).aedState };

        const eventNumber = isEventQueue ? (data as EventQueueRowData).eventNumber : undefined;
        const unitCallSign = isUnitQueue ? (data as UnitQueueRowData).callSign : undefined;

        const { elementRef, isDropOver, isDragging } = useDraggableQueueRow({
            entityType,
            entityId: data.id,
            eventNumber,
            unitCallSign,
        });

        const eventBackgroundProps = getEventQueueRowBackgroundProps(
            queueStore,
            aedStore,
            data,
            isRowSelected,

            eventNotificationPriority,
            isDropOver
        );

        const unitBackgroundProps = getUnitQueueRowBackgroundProps(
            queueStore,
            data,
            isRowSelected,
            isAlerting,
            eventNotificationPriority,
            isDropOver,
            enabledFeatures.unitEmergencyEnabled
        );

        const scheduledBackgroundProps = getScheduledQueueRowBackgroundProps(
            queueStore,
            isRowSelected,
            isDropOver
        );

        const backgroundProps = {
            color: cssVar('arc.colors.text.primary'),
            ...eventBackgroundProps,
            ...unitBackgroundProps,
            ...scheduledBackgroundProps,
        };

        return (
            <Grid
                ref={elementRef}
                onContextMenu={onContextMenu}
                position="relative"
                data-testid={`queue-row-${data.id}`}
                data-row-index={index}
                data-x={queueStore.workspacePanel.xcoordinate}
                data-y={queueStore.workspacePanel.ycoordinate}
                data-queue-height={queueStore.workspacePanel.height}
                data-queue-width={queueStore.workspacePanel.width}
                templateColumns="var(--columns)"
                onClick={onClickMemo}
                onDoubleClick={onDoubleClickMemo}
                borderBottom="1px solid"
                borderColor="border.default"
                opacity={isDragging ? 0.1 : 1}
                {...backgroundProps}
                {...aedStateAttr}
            >
                <EventNotificationsEventIdProvider
                    eventId={getEventQueueRowData(data as UnitQueueRowData)?.id}
                >
                    <EventNotificationsEntityIdProvider
                        entityId={getEventQueueRowData(data as UnitQueueRowData)?.id}
                    >
                        {queueStore.workspacePanel.queue.pinBackupsToPrimaryUnits &&
                        (data as UnitQueueRowData).hasBackups ? (
                            <PinToPrimaryBottomBorder data-testid="pin-to-primary-bottom-border" />
                        ) : null}
                        {queueColumns.map(({ columnDefinition: definition }, index: number) =>
                            queueStore.workspacePanel.queue.pinBackupsToPrimaryUnits &&
                            (data as UnitQueueRowData).backingUpUnitQueueView &&
                            columnDefinitionEnum[definition].omitWhenBackupPinnedToPrimary ? (
                                <QueueCellProvider
                                    eventId={0}
                                    index={index}
                                    key={definition}
                                    columnDefinitionValue={columnDefinitionEnum[definition]}
                                >
                                    <BaseQueueCell />
                                </QueueCellProvider>
                            ) : (
                                <QueueCell
                                    columnDefinitionName={definition}
                                    data={data}
                                    key={definition}
                                    index={index}
                                />
                            )
                        )}
                    </EventNotificationsEntityIdProvider>
                </EventNotificationsEventIdProvider>
            </Grid>
        );
    })
);
